import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { PauseCircleMajor, PlayCircleMajor } from '@shopify/polaris-icons'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { TeamMemberDetails } from 'src/interfaces/team'
import { DeactivateTeamMember, ActivateTeamMember } from 'src/constants/actionTypes'

interface Props {
  teamMemberDetails: TeamMemberDetails
  onRefresh(): Promise<void> | void
  onClose(): void
}

const TeamMemberDetailsActionsComponent = ({ teamMemberDetails, onRefresh, onClose }: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false)
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false)

  const openActivateModal = useCallback(() => {
    setIsActivateModalOpen(true)
  }, [])

  const closeActivateModal = useCallback(() => {
    setIsActivateModalOpen(false)
  }, [])

  const openDeactivateModal = useCallback(() => {
    setIsDeactivateModalOpen(true)
  }, [])

  const closeDeactivateModal = useCallback(() => {
    setIsDeactivateModalOpen(false)
  }, [])

  const handleActivateConfirmSubmit = useCallback(async (): Promise<void> => {
    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ActivateTeamMember.request({
          teamMemberId: teamMemberDetails.id,
        }),
      )

      showToast('User activated!', { variant: 'success' })

      await onRefresh()

      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)

      closeActivateModal()
    }
  }, [teamMemberDetails.id])

  const handleDeactivateConfirmSubmit = useCallback(async (): Promise<void> => {
    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        DeactivateTeamMember.request({
          teamMemberId: teamMemberDetails.id,
        }),
      )

      showToast('User deactivated!', { variant: 'success' })

      await onRefresh()

      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)

      closeDeactivateModal()
    }
  }, [teamMemberDetails.id])

  const handleActionClick = useCallback(() => {
    if (teamMemberDetails.disabled) {
      openActivateModal()
    } else {
      openDeactivateModal()
    }
  }, [teamMemberDetails.disabled])

  return (
    <Fragment>
      <div css={tw`flex flex-col p-4 pb-4`}>
        <div css={tw`w-full h-px bg-[#EDEDED] mb-4`} />
        <Button
          color="grey"
          size="small"
          endIcon={
            teamMemberDetails.disabled ? (
              <PlayCircleMajor width={20} height={20} />
            ) : (
              <PauseCircleMajor width={20} height={20} />
            )
          }
          onClick={handleActionClick}
          loading={isLoading}
          css={tw`w-full`}
        >
          {teamMemberDetails.disabled ? 'Activate user' : 'Deactivate user'}
        </Button>
      </div>
      {isActivateModalOpen && (
        <SensitiveActionModal
          isOpen={isActivateModalOpen}
          title="Activate user?"
          text="Are you sure you want to activate user?"
          color="green"
          submitLabel="Activate"
          onClose={closeActivateModal}
          onSubmit={handleActivateConfirmSubmit}
        />
      )}
      {isDeactivateModalOpen && (
        <SensitiveActionModal
          isOpen={isDeactivateModalOpen}
          title="Deactivate user?"
          text="Are you sure you want to deactivate user?"
          color="red"
          submitLabel="Deactivate"
          onClose={closeDeactivateModal}
          onSubmit={handleDeactivateConfirmSubmit}
        />
      )}
    </Fragment>
  )
}

export const TeamMemberDetailsActions = memo(TeamMemberDetailsActionsComponent)
